// Portal Login Page — credentials -> mandatory MFA (setup or verify)
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { Loader2, AlertCircle, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MfaSetup, MfaVerify, RecoveryCodes, PasskeyVerify } from '../components/MfaScreens';
import { startAuthentication } from '@simplewebauthn/browser';

export function LoginPage() {
  const navigate = useNavigate();
  const { user, login, loading, setUser } = useAuth();

  const [step, setStep] = useState('credentials'); // credentials | mfa-setup | mfa-verify | recovery | passkey
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // MFA state
  const [setupToken, setSetupToken] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [passkeyToken, setPasskeyToken] = useState('');

  useEffect(() => {
    document.title = 'Calyvra CRM';
  }, []);

  useEffect(() => {
    if (user) navigate('/portal');
  }, [user, navigate]);

  const handleCredentials = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsSubmitting(true);
    try {
      const data = await login(email, password);
      if (data.access_token && data.user) {
        setUser(data.user);
        navigate('/portal');
      } else if (data.mfa_setup_required) {
        setSetupToken(data.setup_token);
        const s = await portalApi.mfaSetup(data.setup_token);
        setSetupData(s);
        setStep('mfa-setup');
      } else if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        if (data.passkey_available) {
          setPasskeyToken(data.mfa_token);
          setStep('passkey');
        } else {
          setStep('mfa-verify');
        }
      }
    } catch (err) {
      setLoginError(err.message || 'Invalid email or password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnable = async () => {
    setLoginError('');
    setIsSubmitting(true);
    try {
      const data = await portalApi.mfaEnable(setupToken, code);
      setPendingUser(data.user);
      setCode('');
      setStep('recovery');
    } catch (err) {
      setLoginError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async () => {
    setLoginError('');
    setIsSubmitting(true);
    try {
      const data = await portalApi.mfaVerify(mfaToken, code);
      setUser(data.user);
      navigate('/portal');
    } catch (err) {
      setLoginError(err.message || 'Invalid verification code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasskeyVerify = async () => {
    setLoginError('');
    setIsSubmitting(true);
    try {
      const options = await portalApi.passkeyLoginOptions(passkeyToken);
      const assertion = await startAuthentication({ optionsJSON: options });
      const data = await portalApi.passkeyLoginVerify(passkeyToken, assertion);
      setUser(data.user);
      navigate('/portal');
    } catch (err) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Passkey prompt was cancelled or timed out.'
        : (err.message || 'Passkey verification failed.');
      setLoginError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUseCodeInstead = () => {
    setLoginError('');
    setCode('');
    setStep('mfa-verify');
  };

  const finishSetup = () => {
    setUser(pendingUser);
    navigate('/portal');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 bg-slate-950">
      {/* Brand panel */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950 px-8 py-10 lg:px-14 lg:py-16 flex flex-col justify-between">
        {/* ambient glows + grid */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-indigo-500/10 blur-3xl" />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '44px 44px' }}
        />
        <motion.div
          initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="relative"
        >
          <img
            src="/calyvra-logo-portal-horizontal.png" alt="Calyvra Insurance"
            className="h-10 lg:h-12 w-auto"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="relative hidden lg:block max-w-md"
        >
          <h2 className="text-3xl font-bold text-white">Calyvra CRM</h2>
          <p className="mt-3 text-xs uppercase tracking-[0.25em] text-slate-400">
            Customer Relationship Management
          </p>
        </motion.div>

        <p className="relative text-xs text-slate-400/70 mt-8">
          Internal use only · Authorized personnel.
        </p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-4 py-10 lg:py-0">
        <motion.div
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
          className="w-full max-w-md"
        >
          <div className="relative rounded-2xl border border-white/10 bg-slate-800/60 backdrop-blur-xl p-8 shadow-2xl shadow-black/40">
            {/* subtle glow behind card */}
            <div className="pointer-events-none absolute -inset-0.5 -z-10 rounded-2xl bg-gradient-to-b from-sky-500/10 to-transparent blur" />

            {step === 'credentials' && (
              <>
                <h1 className="text-2xl font-bold text-white">Welcome back</h1>
                <p className="text-sm text-slate-400 mt-1 mb-6">Sign in to continue</p>
                {loginError && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3 text-red-400" data-testid="login-error">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <p className="text-sm">{loginError}</p>
                  </div>
                )}
                <form onSubmit={handleCredentials} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-300">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="email" type="email" placeholder="you@example.com"
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        className="h-11 pl-10 bg-slate-900/70 border-slate-700 text-white placeholder:text-slate-500 focus:border-sky-500 focus:ring-sky-500/20"
                        required data-testid="login-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-300">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        className="h-11 pl-10 pr-10 bg-slate-900/70 border-slate-700 text-white placeholder:text-slate-500 focus:border-sky-500 focus:ring-sky-500/20"
                        required data-testid="login-password"
                      />
                      <button
                        type="button" onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        data-testid="toggle-password"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit" disabled={isSubmitting}
                    className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl shadow-lg shadow-sky-500/25 hover:shadow-sky-500/40 hover:-translate-y-0.5 transition-all duration-300"
                    data-testid="login-submit"
                  >
                    {isSubmitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</>) : 'Sign in'}
                  </Button>
                </form>
              </>
            )}

            {step === 'mfa-setup' && (
              <MfaSetup setupData={setupData} code={code} setCode={setCode} onSubmit={handleEnable} submitting={isSubmitting} error={loginError} />
            )}

            {step === 'mfa-verify' && (
              <MfaVerify code={code} setCode={setCode} onSubmit={handleVerify} submitting={isSubmitting} error={loginError} useRecovery={useRecovery} setUseRecovery={setUseRecovery} />
            )}

            {step === 'recovery' && (
              <RecoveryCodes codes={setupData?.recovery_codes} onDone={finishSetup} />
            )}

            {step === 'passkey' && (
              <PasskeyVerify onVerify={handlePasskeyVerify} onUseCode={handleUseCodeInstead} submitting={isSubmitting} error={loginError} />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default LoginPage;
